import { decode } from "base64-arraybuffer";
import { StatusCodes } from "http-status-codes";
import fileDownload from "js-file-download";
import moment from "moment";

import { API_BASE_URL } from "../configuration/config";
import { BaseService } from "./base-service";
import { CSRFService } from "./csrf-service";
import { UserPermissions } from "./user-permissions";


export const LICENSE_UPLOAD_TEMPLATE_FILE_NAME = "license-upload-template.xlsx";
export const ORDERS_UPLOAD_TEMPLATE_FILE_NAME = "orders-upload-template.xlsx";
export const ORDERS_FILE_NAME = "orders";

/**
 * Result of initiating the new-orders export via the Shop new-orders export endpoint.
 */
export type DownloadNewOrdersOutcome =
  | { outcome: "NO_ORDERS" }
  | { outcome: "DOWNLOADED"; fileName: string; orderIds: string[] };

/**
 * A service for downloading the license template 
 */
export class FilesService extends BaseService {


  /**
   * Singleton instance for download service
   */
  public static instance = new FilesService(API_BASE_URL);

  public async downloadLicenseUploadTemplate() {
    const response = await this.fetch("/get-license-upload-template");
    if (response.status !== StatusCodes.OK) {
      throw new Error("An error occurred when downloading the license upload template.");
    }
    const fileContent = await response.text();

    fileDownload(decode(fileContent), LICENSE_UPLOAD_TEMPLATE_FILE_NAME);
  }

  public async downloadOrdersUploadTemplate() {
    const response = await this.fetch("/get-orders-upload-template");
    if (response.status !== StatusCodes.OK) {
      throw new Error("An error occurred when downloading the orders upload template.");
    }
    const fileContent = await response.text();

    fileDownload(decode(fileContent), ORDERS_UPLOAD_TEMPLATE_FILE_NAME);
  }


  public downloadTinventFile = async (fileIdGuid: string, fileName: string, type: string) => {
    const response = await this.fetch(`/tinvent/file/presignedurl/${fileIdGuid}?type=${type}`);
   if (response.status !== StatusCodes.OK) {
      throw new Error(`An error occurred when downloading the ${type} file.`);
    }
    const fileContent = await response.text();
    fileDownload(decode(fileContent), fileName);
  }

  public async downloadOrders() {
    const response: Response = await this.fetch(`/ito/orders/download?status=APPROVED&shouldUpdateToStatus=PROCESSING`);
    if (response.status !== StatusCodes.OK) {
      throw new Error(`An error occurred when downloading orders.`);
    }

    const fileName = this.getFileNameFromHeader(response.headers) ?? this.createFileName();

    const fileContent = await response.json();
    fileDownload(decode(fileContent), fileName);
  }

  public downloadOrdersHistory = async (fileIdGuid: string, fileName: string, type: string) => {
    const response = await this.fetch(`/ito/file/presignedurl/${fileIdGuid}?type=${type}`);
    if (response.status !== StatusCodes.OK) {
      throw new Error(`An error occurred when downloading the orders file.`);
    }
    const fileContent = await response.text();
    fileDownload(decode(fileContent), fileName);
  }

  /* istanbul ignore next */
  public async uploadBulkFile(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const content = e.target?.result as ArrayBuffer;

        const csrfService = new CSRFService();
        csrfService.getToken().then((token: string) => {
          try {
            // fetch
            this.fetch('/tinvent/bulk', {
              method: 'POST',
              body: content,
              headers: {
                "Content-Type": file.type,
                "X-Tinvent-File-Name": file.name,
                "X-Tinvent-Uploaded-By": UserPermissions.instance.username,
                "x-csrf-token": token
              }
            }).then((response) => {
              resolve(response.ok);
            });
          } catch (error) { resolve(false); }
        });
      };

      reader.onerror = () => { resolve(false); };

      reader.readAsArrayBuffer(file);
    });
  }

  /* istanbul ignore next */
  public async uploadBulkOrders(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const content = e.target?.result as ArrayBuffer;

        const csrfService = new CSRFService();
        csrfService.getToken().then((token: string) => {
          try {
            // fetch
            this.fetch('/ito/files/bulkupload', {
              method: 'POST',
              body: content,
              headers: {
                "Content-Type": file.type,
                "X-Ito-File-Name": file.name,
                "x-csrf-token": token
              }
            }).then((response) => {
              resolve(response.ok);
            });
          } catch (error) { resolve(false); }
        });
      };

      reader.onerror = () => { resolve(false); };

      reader.readAsArrayBuffer(file);
    });
  }

  public getFileNameFromHeader(headers: Headers): string | undefined {
    let contentHeader: string | null = null;
    if (headers) {
      contentHeader = headers.get("Content-Disposition") || headers.get("content-disposition");
    }

    let fileName: string | undefined = undefined;
    if (contentHeader) {
      // Gets the file name from the header:
      // From 'Content-Disposition': 'attachment; filename="orders_20231002154008223.xlsx"'
      // Returns orders_20231002154008223.xlsx
      const regexFilename = /filename="(?<fileInfo>.*)"/
      const matchResult = regexFilename.exec(contentHeader);

      if (matchResult?.groups && matchResult?.groups?.fileInfo) {
        fileName = matchResult?.groups?.fileInfo;
      }
    }

    return fileName;
  }

  public createFileName(): string {
    const dateFormat = (moment(new Date())).utc().format('YYYYMMDDHHmmssSSS');
    const fileName = `${ORDERS_FILE_NAME}_${dateFormat}.xlsx`;
    return fileName;
  }

  public async exportFilteredOrders(filters: { [key: string]: string[] }): Promise<string> {
    // Build query string from filters
    const params = new URLSearchParams();
    for (const [key, values] of Object.entries(filters)) {
      for (const value of values) {
        params.append(key, value);
      }
    }

    // Fetch CSRF token
    const csrfService = new CSRFService();
    const csrfToken = await csrfService.getToken();

    // Initiate export
    const response = await this.fetch(`/ito/orders/export?${params.toString()}`, {
      method: 'POST',
      headers: { 'x-csrf-token': csrfToken },
    });
    if (response.status !== 202) {
      throw new Error("Failed to initiate export");
    }
    const { jobId } = await response.json();

    // Poll until done (with elapsed-time-based timeout).
    // The ITO export Lambda has a 15-minute execution timeout; the poll window matches it
    // exactly — no extra buffer is needed since we measure wall-clock time, not attempt count.
    const POLL_TIMEOUT_MS = 900_000; // 15 minutes
    const MAX_CONSECUTIVE_NETWORK_ERRORS = 3;

    // Progressive backoff schedule: wait before each successive poll attempt.
    // Index = poll number (0-based), value = delay in ms before that poll.
    const BACKOFF_SCHEDULE_MS = [2000, 10_000, 20_000, 30_000, 60_000, 120_000, 180_000];
    const getBackoffMs = (pollIndex: number): number =>
      BACKOFF_SCHEDULE_MS[Math.min(pollIndex, BACKOFF_SCHEDULE_MS.length - 1)];

    const startTime = Date.now();
    let pollCount = 0;
    let consecutiveNetworkErrors = 0;
    let status = "PROCESSING";
    let result: any = null;

    while (status === "PROCESSING") {
      const elapsed = Date.now() - startTime;
      if (elapsed >= POLL_TIMEOUT_MS) {
        throw new Error("Export timed out");
      }
      const remaining = POLL_TIMEOUT_MS - elapsed;
      const delay = Math.min(getBackoffMs(pollCount), remaining);
      await new Promise((resolve) => setTimeout(resolve, delay));
      pollCount++;

      let pollResponse: Response;
      try {
        pollResponse = await this.fetch(`/ito/orders/export/${jobId}`);
      } catch {
        // Network-level failure (DNS, connection refused, aborted request, etc.)
        consecutiveNetworkErrors++;
        if (consecutiveNetworkErrors >= MAX_CONSECUTIVE_NETWORK_ERRORS) {
          throw new Error("Export poll failed: network error");
        }
        continue;
      }

      // Fetch resolved (got an HTTP response) — reset the network-error streak.
      consecutiveNetworkErrors = 0;

      if (!pollResponse.ok) {
        // A permanent client error (4xx — e.g. 404 job-not-found, 400 bad jobId, 403) will
        // never recover on retry, so fail fast instead of spinning for the full poll window
        // and reporting a misleading "Export timed out". 429 is excluded — throttling is
        // transient and recovers on retry, so it falls through to the same treatment as a 5xx.
        if (pollResponse.status >= 400 && pollResponse.status < 500 && pollResponse.status !== 429) {
          throw new Error(`Export poll failed: ${pollResponse.status}`);
        }
        // Transient server error (e.g. 500, 429) — keep polling within the time budget instead
        // of treating it as a terminal failure.
        continue;
      }
      result = await pollResponse.json();
      status = result.status;
    }

    if (status === "DONE" && result.downloadUrl) {
      // Navigate directly to the presigned S3 URL instead of fetch()ing it — navigations are
      // exempt from CORS but fetch() isn't, and the export bucket has no CORS policy.
      const downloadedFileName = result.fileName || "orders-export.xlsx";
      const anchor = document.createElement("a");
      anchor.href = result.downloadUrl;
      anchor.download = downloadedFileName;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      return downloadedFileName;
    }

    throw new Error(result?.errorMessage || `Export failed with status: ${status}`);
  }

  public async downloadNewOrdersViaShopEndpoint(): Promise<DownloadNewOrdersOutcome> {
    const csrfService = new CSRFService();
    const token = await csrfService.getToken();
    const response = await this.fetch(`/orders/new/export`, {
      method: 'POST',
      headers: { "x-csrf-token": token },
    });
    // Do not read the body on a 200 (empty body).
    if (response.status === StatusCodes.OK) {
      return { outcome: "NO_ORDERS" };
    }
    if (response.status !== 202) {
      throw new Error("Failed to initiate export");
    }
    const { jobId } = await response.json();

    const MAX_POLL_ATTEMPTS = 150;
    const MAX_CONSECUTIVE_NETWORK_ERRORS = 3;
    let attempts = 0;
    let consecutiveNetworkErrors = 0;
    let status = "PROCESSING";
    let result: any = null;
    while (status === "PROCESSING") {
      if (attempts >= MAX_POLL_ATTEMPTS) {
        throw new Error("Export timed out");
      }
      attempts++;
      await new Promise((resolve) => setTimeout(resolve, 2000));

      let pollResponse: Response;
      try {
        pollResponse = await this.fetch(`/ito/orders/export/${jobId}`);
      } catch {
        consecutiveNetworkErrors++;
        if (consecutiveNetworkErrors >= MAX_CONSECUTIVE_NETWORK_ERRORS) {
          throw new Error("Export poll failed: network error");
        }
        continue;
      }

      consecutiveNetworkErrors = 0;

      if (!pollResponse.ok) {
        if (pollResponse.status >= 400 && pollResponse.status < 500 && pollResponse.status !== 429) {
          throw new Error(`Export poll failed: ${pollResponse.status}`);
        }
        continue;
      }
      result = await pollResponse.json();
      status = result.status;
    }

    if (status === "DONE" && result.downloadUrl) {
      const downloadedFileName = result.fileName || "orders-export.xlsx";
      const orderIds: string[] = Array.isArray(result.orderIds) ? result.orderIds : [];
      const anchor = document.createElement("a");
      anchor.href = result.downloadUrl;
      anchor.download = downloadedFileName;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      return { outcome: "DOWNLOADED", fileName: downloadedFileName, orderIds };
    }

    throw new Error(result?.errorMessage || "Export failed");
  }

}
